import type { AuthConfig, AuthUser, Locale } from '../types';

const SS_AUTH = 'sas.auth';
const SS_USER = 'sas.user';

/**
 * IMPORTANT: this is a CLIENT-SIDE access gate, not real authentication.
 * The app is fully static, so anyone can bypass this by reading the bundle or
 * fetching the data files directly. For enforced access put the site behind
 * Cloudflare Access, nginx Basic Auth, or a real backend. See DEPLOY.md.
 */

async function sha256(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function authRequired(cfg: AuthConfig | undefined): boolean {
  return Boolean(cfg && cfg.users && cfg.users.length > 0);
}

export function isAuthed(): boolean {
  try {
    return sessionStorage.getItem(SS_AUTH) === '1';
  } catch {
    return false;
  }
}

export function currentUserName(locale: Locale): string {
  try {
    return sessionStorage.getItem(`${SS_USER}.${locale}`) || sessionStorage.getItem(SS_USER) || '';
  } catch {
    return '';
  }
}

export async function verify(
  cfg: AuthConfig,
  username: string,
  password: string,
): Promise<AuthUser | null> {
  const u = cfg.users.find((x) => x.username.toLowerCase() === username.trim().toLowerCase());
  if (!u) return null;
  const hash = await sha256(u.salt + password);
  return hash === u.hash ? u : null;
}

export function grant(user: AuthUser): void {
  try {
    sessionStorage.setItem(SS_AUTH, '1');
    sessionStorage.setItem(SS_USER, user.name_en);
    sessionStorage.setItem(`${SS_USER}.ar`, user.name_ar);
    sessionStorage.setItem(`${SS_USER}.en`, user.name_en);
  } catch {
    /* ignore */
  }
}

export function logout(): void {
  try {
    sessionStorage.removeItem(SS_AUTH);
    sessionStorage.removeItem(SS_USER);
    sessionStorage.removeItem(`${SS_USER}.ar`);
    sessionStorage.removeItem(`${SS_USER}.en`);
  } catch {
    /* ignore */
  }
  location.hash = '';
  location.reload();
}
